/* eslint-disable local/ensure-wac-token-usage */
import type {
	AnchorHTMLAttributes,
	ButtonHTMLAttributes,
	DetailedHTMLProps,
	FC,
	MouseEvent,
} from 'react';

import { css, cssMap } from '@compiled/react';

import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { useTryIntent } from '@/components/try-intent/src/use-try-intent.ts';
import { wacToken } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import {
	isLocalizableHref,
	useLocalizedHref,
} from '@/libraries/web-framework/src/localization/use-localized-href';
import { DataEventProps } from '@/libraries/wpl/foundations/src/data-event-props';
import { useWacRefresh } from '@/utils/wac-refresh/wac-refresh-context';

// Keep `large` and `xlarge` as legacy inputs so existing callers render without migration.
export type ButtonSize = 'default' | 'large' | 'xlarge' | 'small';
type NormalizedButtonSize = 'default' | 'small';

const normalizeButtonSize = (size: ButtonSize): NormalizedButtonSize =>
	size === 'small' ? 'small' : 'default';

export type ButtonColor = 'blue' | 'yellow' | 'saffron' | 'white';
export type ButtonColorAgainst = 'white' | 'black' | 'other';
export type ButtonVariant = 'primary' | 'primary_alt' | 'secondary';

export type ResponsiveButtonSizes = {
	mobile: ButtonSize;
	tablet: ButtonSize;
	desktop: ButtonSize;
	xl_desktop: ButtonSize;
};

// CSS Map does not allow tokens
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buttonVariantStyles: { [key: string]: any } = cssMap({
	primary: {
		color: '#ffffff',
		backgroundColor: '#1868DB',
		border: '2px solid transparent',

		'&:hover': {
			// eslint-disable-next-line local/ensure-wac-token-usage
			backgroundColor: '#123263',
		},
	},
	primary_alt: {
		color: '#101214',
		backgroundColor: '#FCA700',
		border: '2px solid transparent',

		'&:hover': {
			// eslint-disable-next-line local/ensure-wac-token-usage
			backgroundColor: '#FFE48F',
		},
	},
	secondary_against_white: {
		color: '#101214',
		backgroundColor: 'transparent',
		border: '2px solid #101214',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'#arrow-icon, path': {
			fill: '#101214',
		},

		'&:hover': {
			// eslint-disable-next-line local/ensure-wac-token-usage
			backgroundColor: '#100F0E10',
		},
	},
	secondary_against_black: {
		color: '#ffffff',
		backgroundColor: 'transparent',
		border: '2px solid #ffffff',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'#arrow-icon, path': {
			fill: '#ffffff',
		},

		'&:hover': {
			// eslint-disable-next-line local/ensure-wac-token-usage
			backgroundColor: '#2B2C2F',
		},
	},
	secondary_against_other: {
		color: '#ffffff',
		backgroundColor: 'transparent',
		border: '2px solid #ffffff',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'#arrow-icon, path': {
			fill: '#ffffff',
		},

		'&:hover': {
			// eslint-disable-next-line local/ensure-wac-token-usage
			background: '#2B2C2F33',
			backgroundBlendMode: 'multiply',
		},
	},
});

const baseStyles = css({
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	display: 'inline-flex',
	fontWeight: 500,
	fontFamily: wacToken('font.charlie.text'),
	textAlign: 'center',
	textDecoration: 'none',
	borderRadius: wacToken('corner.radius.pill'),

	'&:hover': {
		cursor: 'pointer',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['#arrow-icon']: {
			transform: `translateX(${token('space.050')})`,
		},
	},

	'&:focus-visible': {
		outline: 'none',
	},
});

// CSS Map does not allow tokens
// Focus styles map for different background types
const focusStyles = cssMap({
	lightBackground: {
		'&:focus-visible': {
			// eslint-disable-next-line local/ensure-wac-token-usage
			outline: '2px solid #1868DB',
			outlineOffset: '4px',
		},
	},
	otherBackground: {
		'&:focus-visible': {
			// eslint-disable-next-line local/ensure-wac-token-usage
			outline: '2px solid #FFFFFF',
			outlineOffset: '4px',
		},
	},
});

// CSS Map does not allow tokens
const fontAndHeightStyles = cssMap({
	default: {
		fontSize: '1.25rem',
		lineHeight: '1.5rem',
		height: 'min-content',
		minHeight: '48px',
	},
	small: {
		fontSize: '1rem',
		lineHeight: '1.25rem',
		height: 'min-content',
		minHeight: '40px',
	},
});

const noMaxWidthStyle = css({
	maxWidth: 'none',
});

const disabledStyles = css({
	backgroundColor: wacToken('color.neutral.light400'),
	color: wacToken('color.neutral.light0'),
	borderColor: 'transparent',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['#arrow-icon, path']: {
		fill: wacToken('color.neutral.light0'),
	},
	['&:hover']: {
		cursor: 'not-allowed',
		backgroundColor: wacToken('color.neutral.light400'),
		color: wacToken('color.neutral.light0'),
		borderColor: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		['#arrow-icon, path']: {
			fill: wacToken('color.neutral.light0'),
			transform: 'none',
		},
	},
});

const loadingStyles = css({
	['&:hover']: {
		cursor: 'not-allowed',
		backgroundColor: 'none',
	},
});

const animatedStyles = css({
	['&:active']: {
		scale: '97%',
		transition: `scale ${wacToken('motion.duration.100ms')} ${wacToken(
			'motion.easing.ease40-in-out',
		)}`,
	},
	transition: `scale ${wacToken('motion.duration.150ms')} ${wacToken(
		'motion.easing.ease40-in-out',
	)}, background-color ${wacToken('motion.duration.150ms')} ${wacToken(
		'motion.easing.ease40-in-out',
	)}`,
});

const defaultSizeStyles = css({
	padding: '10px 22px',
	minWidth: '120px',
	maxWidth: '368px',
});

const smallSizeStyles = css({
	padding: '8px 22px',
	minWidth: '100px',
	maxWidth: '368px',
});

// Responsive size styles per viewport using cssMap.
// Compiled CSS requires media query keys to be static string literals.
// The strings below are the exact values of the @atlaskit/primitives media constants:
//   mobile     → media.below.sm = "@media not all and (min-width: 48rem)"
//   tablet     → media.only.sm  = "@media (min-width: 48rem) and (max-width: 63.99rem)"
//   desktop    → media.only.md  = "@media (min-width: 64rem) and (max-width: 89.99rem)"
//   xl_desktop → media.above.lg = "@media (min-width: 90rem)"
const mobileResponsiveSizeStylesMap = cssMap({
	default: {
		'@media not all and (min-width: 48rem)': {
			padding: '10px 26px', // need to account for 2px transparent border
			minWidth: '120px',
			maxWidth: '368px',
			fontSize: '20px',
			fontWeight: '400',
			lineHeight: '24px',
			height: 'min-content',
			minHeight: '48px',
			['#arrow-icon']: {
				marginBlockStart: '2px',
			},
		},
	},
	small: {
		'@media not all and (min-width: 48rem)': {
			padding: '8px 22px', // need to account for 2px transparent border
			minWidth: '100px',
			maxWidth: '368px',
			fontSize: '16px',
			fontWeight: '400',
			lineHeight: '20px',
			height: 'min-content',
			minHeight: '40px',
			['#arrow-icon']: {
				height: '10.2px',
				marginBlockStart: '2px',
			},
		},
	},
});

const tabletResponsiveSizeStylesMap = cssMap({
	default: {
		'@media (min-width: 48rem) and (max-width: 63.99rem)': {
			padding: '10px 26px', // need to account for 2px transparent border
			minWidth: '120px',
			maxWidth: '368px',
			fontSize: '20px',
			fontWeight: '400',
			lineHeight: '24px',
			height: 'min-content',
			minHeight: '48px',
			['#arrow-icon']: {
				marginBlockStart: '2px',
			},
		},
	},
	small: {
		'@media (min-width: 48rem) and (max-width: 63.99rem)': {
			padding: '8px 22px', // need to account for 2px transparent border
			minWidth: '100px',
			maxWidth: '368px',
			fontSize: '16px',
			fontWeight: '400',
			lineHeight: '20px',
			height: 'min-content',
			minHeight: '40px',
			['#arrow-icon']: {
				height: '10.2px',
				marginBlockStart: '2px',
			},
		},
	},
});

const desktopResponsiveSizeStylesMap = cssMap({
	default: {
		'@media (min-width: 64rem) and (max-width: 89.99rem)': {
			padding: '10px 26px', // need to account for 2px transparent border
			minWidth: '120px',
			maxWidth: '368px',
			fontSize: '20px',
			fontWeight: '400',
			lineHeight: '24px',
			height: 'min-content',
			minHeight: '48px',
			['#arrow-icon']: {
				marginBlockStart: '2px',
			},
		},
	},
	small: {
		'@media (min-width: 64rem) and (max-width: 89.99rem)': {
			padding: '8px 22px', // need to account for 2px transparent border
			minWidth: '100px',
			maxWidth: '368px',
			fontSize: '16px',
			fontWeight: '400',
			lineHeight: '20px',
			height: 'min-content',
			minHeight: '40px',
			['#arrow-icon']: {
				height: '10.2px',
				marginBlockStart: '2px',
			},
		},
	},
});

const xlDesktopResponsiveSizeStylesMap = cssMap({
	default: {
		'@media (min-width: 90rem)': {
			padding: '10px 26px', // need to account for 2px transparent border
			minWidth: '120px',
			maxWidth: '368px',
			fontSize: '20px',
			fontWeight: '400',
			lineHeight: '24px',
			height: 'min-content',
			minHeight: '48px',
			['#arrow-icon']: {
				marginBlockStart: '2px',
			},
		},
	},
	small: {
		'@media (min-width: 90rem)': {
			padding: '8px 22px', // need to account for 2px transparent border
			minWidth: '100px',
			maxWidth: '368px',
			fontSize: '16px',
			fontWeight: '400',
			lineHeight: '20px',
			height: 'min-content',
			minHeight: '40px',
			['#arrow-icon']: {
				height: '10.2px',
				marginBlockStart: '2px',
			},
		},
	},
});

const awf1Styles = css({
	fontFamily: token('font.family.code'),
	textTransform: 'uppercase',
	paddingBlock: `${token('space.075')} ${token('space.075')}`,
});

export type ButtonProps = {
	size?: ButtonSize;
	responsiveSize?: ResponsiveButtonSizes;
	variant?: ButtonVariant;
	colorAgainst?: ButtonColorAgainst;
	className?: string;
	dataEventProps?: DataEventProps;
	isLoading?: boolean;
	isDisabled?: boolean;
	ariaLabelledby?: string;
} & (
	| DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
	| DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>
);

function isLinkArgs(props: unknown): props is AnchorHTMLAttributes<HTMLAnchorElement> {
	return 'href' in (props as AnchorHTMLAttributes<HTMLAnchorElement>);
}

const isSignInUrl = (href: string) => {
	const baseUrl = process.env['NEXT_PUBLIC_IDENTITY_BASE_URL'];
	const stargateBaseUrl = process.env['NEXT_PUBLIC_API_PRIVATE_BASE_URL'];
	const continueUrl = stargateBaseUrl + '/start/authredirect';
	const signInUrl = `${baseUrl}/login?continue=${encodeURIComponent(continueUrl)}`;

	// Check if href matches sign-in URL with or without application parameter, or is start-trial
	return (
		href === signInUrl || href.startsWith(signInUrl + '&application=') || href === '#start-trial'
	);
};

const isHyperlinkRef = (href: string) => {
	return href.startsWith('#');
};

const isNonSigninOrWacUrl = (href: string) => {
	return !isLocalizableHref(href) && !isSignInUrl(href) && !isHyperlinkRef(href);
};

const TRY_CLOUD_SIGNUP_HREF_PATTERN = /\/try\/cloud\/signup(?:[/?#]|$)/;

export const isTryCloudSignupHref = (href: string | undefined) => {
	return href ? TRY_CLOUD_SIGNUP_HREF_PATTERN.test(href) : false;
};

const getButtonVariant = (
	variant: ButtonVariant,
	colorAgainst: ButtonColorAgainst | undefined,
): string => {
	if (variant === 'primary' || variant === 'primary_alt') {
		return variant;
	}
	if (colorAgainst === undefined) {
		return 'secondary_against_white';
	}

	return `secondary_against_${colorAgainst}`;
};

const isLightBackground = (colorAgainst: ButtonColorAgainst | undefined): boolean => {
	return colorAgainst === 'white' || colorAgainst === undefined;
};

const arrowIcon = (
	<svg
		css={css({
			flexShrink: 0,
			marginInlineStart: token('space.100'),
			transition: `transform ${wacToken('motion.duration.250ms')} ${wacToken(
				'motion.easing.ease-both',
			)}`,
		})}
		id="arrow-icon"
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="12"
		viewBox="0 0 16 12"
		aria-hidden="true"
	>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M9.1432 0.355029C9.61658 -0.118343 10.3839 -0.118343 10.8573 0.355029L15.6452 5.143C15.7172 5.21496 15.777 5.29436 15.8271 5.37785C15.836 5.39278 15.8462 5.40702 15.8545 5.42236L15.8612 5.43562C15.8922 5.49457 15.9163 5.55608 15.9369 5.61839C15.9398 5.62691 15.9428 5.63534 15.9454 5.64395C15.9526 5.66746 15.9586 5.69116 15.9644 5.71498C15.9686 5.73229 15.9733 5.74947 15.9767 5.76706C15.9994 5.88356 16.0054 6.00249 15.9937 6.12029C15.9883 6.17526 15.9778 6.22872 15.9653 6.28128C15.9593 6.30667 15.9531 6.332 15.9454 6.35704C15.9427 6.36603 15.9389 6.37465 15.936 6.38355C15.9279 6.40785 15.921 6.43263 15.9114 6.45647C15.9096 6.46077 15.9065 6.4645 15.9047 6.46878C15.8818 6.52321 15.8545 6.57499 15.8242 6.62503C15.816 6.63867 15.8094 6.65333 15.8006 6.6667C15.7994 6.66838 15.7979 6.66976 15.7968 6.67144C15.7894 6.68253 15.7808 6.6928 15.7731 6.70363C15.7584 6.72411 15.7447 6.74545 15.7286 6.76519C15.7027 6.79691 15.6748 6.82747 15.6452 6.85705L10.8573 11.645C10.3839 12.1183 9.61657 12.1183 9.1432 11.645C8.66985 11.1717 8.66988 10.4043 9.1432 9.93097L11.862 7.21217H1.21215C0.542727 7.21217 4.7978e-05 6.66943 0 6.00002C0 5.33057 0.542698 4.78788 1.21215 4.78788H11.862L9.1432 2.06908C8.66985 1.59572 8.66988 0.828404 9.1432 0.355029Z"
		/>
	</svg>
);

export const Button: FC<ButtonProps> = ({
	variant = 'primary',
	size = 'default',
	responsiveSize,
	colorAgainst,
	className,
	children,
	dataEventProps,
	isLoading,
	isDisabled = false,
	ariaLabelledby,
	...props
}: // eslint-disable-next-line complexity
ButtonProps) => {
	const isWacRefresh = useWacRefresh();
	const { isAwf1Theme } = useTheme();

	const buttonSize = normalizeButtonSize(variant === 'secondary' && !isWacRefresh ? 'small' : size);

	const isLink = isLinkArgs(props);
	const href = isLink ? props.href : undefined;
	const tryIntent = useTryIntent({
		intentType: 'button',
		productKey: dataEventProps?.['data-product-key'],
	});
	const shouldFireTryIntent = isTryCloudSignupHref(href);

	const fontAndHeightStyle = fontAndHeightStyles[buttonSize];

	const buttonVariant = getButtonVariant(variant, colorAgainst);
	const variantStyles = buttonVariantStyles[buttonVariant];
	const focusStylesKey = isLightBackground(colorAgainst) ? 'lightBackground' : 'otherBackground';
	const buttonFocusStyles = focusStyles[focusStylesKey];

	const handleOnClick = (e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>) => {
		if (isDisabled) {
			e.preventDefault();
		} else {
			if (props.onClick) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				props.onClick(e as any);
			}
			if (shouldFireTryIntent && !e.currentTarget.closest('[data-testid="try-intent-wrapper"]')) {
				tryIntent.onClick();
			}
		}
	};

	const cssList = [
		baseStyles,
		variantStyles,
		fontAndHeightStyle,
		buttonFocusStyles,
		isDisabled && !isLoading && disabledStyles,
		isLoading && loadingStyles,
		isWacRefresh && animatedStyles,
		buttonSize === 'default' && defaultSizeStyles,
		buttonSize === 'small' && smallSizeStyles,
		isWacRefresh &&
			responsiveSize?.mobile &&
			mobileResponsiveSizeStylesMap[normalizeButtonSize(responsiveSize.mobile)],
		isWacRefresh &&
			responsiveSize?.tablet &&
			tabletResponsiveSizeStylesMap[normalizeButtonSize(responsiveSize.tablet)],
		isWacRefresh &&
			responsiveSize?.desktop &&
			desktopResponsiveSizeStylesMap[normalizeButtonSize(responsiveSize.desktop)],
		isWacRefresh &&
			responsiveSize?.xl_desktop &&
			xlDesktopResponsiveSizeStylesMap[normalizeButtonSize(responsiveSize.xl_desktop)],
		isWacRefresh && variant === 'secondary' && noMaxWidthStyle,
		isAwf1Theme && awf1Styles,
	];

	const localizedHref = useLocalizedHref(href ?? '');

	if (isLink) {
		return (
			<a
				className={className}
				css={cssList}
				{...props}
				{...dataEventProps}
				onClick={handleOnClick}
				href={localizedHref}
				target={
					typeof props.target === 'string'
						? props.target
						: props.href &&
							  /* redirecting to new tab if the href is not on WAC and not a Signin link */
							  isNonSigninOrWacUrl(props.href)
							? '_blank'
							: ''
				}
				rel={props.href && isNonSigninOrWacUrl(props.href) ? 'noreferrer' : ''}
				aria-labelledby={ariaLabelledby}
			>
				{children}
				{isWacRefresh && variant === 'secondary' && arrowIcon}
			</a>
		);
	}

	// Primary Blue and Secondary (against anything other than white) should have a white spinner
	const spinnerAppearance =
		variant === 'primary' || (variant === 'secondary' && !!colorAgainst && colorAgainst !== 'white')
			? 'invert'
			: 'inherit';

	return (
		<button
			className={className}
			disabled={isLoading || isDisabled}
			css={cssList}
			type={'button' /* this default value is overridable within the following props */}
			{...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
			{...dataEventProps}
			onClick={handleOnClick}
			aria-labelledby={ariaLabelledby}
		>
			{isLoading ? (
				<Spinner
					interactionName="load"
					label="Loading"
					appearance={spinnerAppearance}
					size={buttonSize === 'small' ? 'small' : 'medium'}
				/>
			) : (
				children
			)}
			{!isLoading && isWacRefresh && variant === 'secondary' && arrowIcon}
		</button>
	);
};
